<template>
  <div
    class="fixed top-4 right-4 w-[min(420px,calc(100vw-2rem))] pointer-events-none"
    style="z-index: 1000000;"
    aria-live="polite"
    aria-relevant="additions removals"
  >
    <TransitionGroup name="toast" tag="div">
      <div
        v-for="toast in toasts"
        :key="toast.id"
        class="pointer-events-auto mb-2"
        @mouseenter="pauseToast(toast.id)"
        @mouseleave="resumeToast(toast.id)"
      >
        <div
          class="relative overflow-hidden rounded-xl border bg-white/95 shadow-lg backdrop-blur-sm"
          :class="typeClasses(toast.type).container"
          role="status"
        >
          <div class="flex items-start gap-3 p-4">
            <div class="mt-0.5 shrink-0">
              <i
                :class="[typeClasses(toast.type).icon, 'text-lg']"
                aria-hidden="true"
              ></i>
            </div>

            <div class="min-w-0 flex-1">
              <div class="text-sm font-medium text-gray-900 leading-snug wrap-break-word">
                {{ toast.message }}
              </div>
            </div>

            <button
              type="button"
              class="ml-2 inline-flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition"
              aria-label="Close notification"
              @click="removeToast(toast.id)"
            >
              <i class="fa fa-times" aria-hidden="true"></i>
            </button>
          </div>

          <!-- progress bar (100 -> 0) -->
          <div class="h-1 w-full bg-gray-100">
            <div
              class="h-1 transition-[width] duration-100 ease-linear"
              :class="typeClasses(toast.type).bar"
              :style="{ width: `${Math.max(0, Math.min(100, toast.progress))}%` }"
            ></div>
          </div>
        </div>
      </div>
    </TransitionGroup>
  </div>
</template>

<script setup lang="ts">
import { useToasts, type ToastType } from "~/composables/useToasts";

const { toasts, removeToast, pauseToast, resumeToast } = useToasts();

const typeClasses = (type: ToastType) => {
  switch (type) {
    case "success":
      return {
        container: "border-green-200 ring-1 ring-green-100",
        icon: "fa fa-check-circle text-green-600",
        bar: "bg-green-500",
      };
    case "error":
      return {
        container: "border-red-200 ring-1 ring-red-100",
        icon: "fa fa-exclamation-circle text-red-600",
        bar: "bg-red-500",
      };
    default:
      return {
        container: "border-blue-200 ring-1 ring-blue-100",
        icon: "fa fa-info-circle text-blue-600",
        bar: "bg-blue-500",
      };
  }
};
</script>

<style scoped>
/* animations */
.toast-enter-active,
.toast-leave-active {
  transition: transform 220ms ease, opacity 220ms ease;
}
.toast-enter-from,
.toast-leave-to {
  opacity: 0;
  transform: translateY(-12px);
}
.toast-move {
  transition: transform 220ms ease;
}
.toast-leave-active {
  position: absolute;
  right: 0;
  width: 100%;
}
</style>
